import React from 'react';
import PropTypes from 'prop-types';
import { cx, css } from 'emotion';
import _ from 'lodash';
import Flexbox from 'flexbox-react';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIENumber from 'components/core/editable/RIENumber';
import RIEUnits from 'components/core/editable/RIEUnits';
import DimensionInputDatasetField from 'components/core/fields/DimensionInputDatasetField';
import { colorLabelBlue, colorVeryLightGrey } from 'styles/base';
import regularIntervalDiagram from 'images/regular_interval.png';
import InfoMessages from 'components/core/help/InfoMessages';

export default class DimensionsForm extends React.Component {
    static propTypes = {
        process: PropTypes.object,
        processErrors: PropTypes.object,
        hasWritePermissions: PropTypes.bool,
        coordinateSystemName: PropTypes.string,
        dimensionName: PropTypes.string,
        dimensionIsTime: PropTypes.bool
    };

    isDimensionType = (type) => {
        return this.props.process.variable_retrieval.coordinate_systems[this.props.coordinateSystemName].dimensions[this.props.dimensionName].gridding_method === type;
    };

    constructor(props) {
        super(props);

        this.boldFont = css`font-weight:900;`;
    }

    getBaseDimensionPath = () => {
        return ['variable_retrieval', 'coordinate_systems', this.props.coordinateSystemName, 'dimensions', this.props.dimensionName];
    };

    getDimension = () => {
        return _.get(this.props.process, this.getBaseDimensionPath());
    };

    getUnitOptions() {
        const options = ProcessUtils.getDimensionCoordinateVariableUnits(this.props.process, this.props.dimensionName, null);
        return Constants.convertArrayToSelectOptions(options);
    }

    onRadioClick = (event) => {
        let newValue = event.target.value;
        if (newValue === 'sameAsInput') {
            newValue = Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING;
        }
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.updateGriddingMethod(this.props.coordinateSystemName, this.props.dimensionName, newValue);
        editor.commit();
    };

    onFieldUpdated = (fieldPath, value) => {
        ProcessUtils.updateField(this.props.process, fieldPath, value);
    };

    onNumberUpdate = (fieldPath, value) => {
        //TODO: probably a way to pull this into the RIE component?
        ProcessUtils.updateField(this.props.process, fieldPath, Number(value));
    };

    renderRegularInterval = () => {
        const baseDimensionPath = this.getBaseDimensionPath();
        const dimension = _.get(this.props.process, baseDimensionPath);
        const diagram = (
            <img
                src={regularIntervalDiagram}
                alt={'Diagram of regular grid interval'}
                className={css`max-width:500px; margin-bottom: 10px;`}
            />
        );
        const innerBoxStyles = css`
            margin: 5px 15px;
            padding: 10px 5px 15px 15px;
            background-color: ${colorVeryLightGrey};
        `;

        const columnStyles = css`
            margin-right: 10px;
            .infobox {
                align-self: flex-start !important;
            }
        `;

        const unitsLabel = ProcessUtils.getDimensionUnitsLabel(dimension);

        return (
            <Flexbox flexDirection={'column'} className={innerBoxStyles}>
                {!this.props.dimensionIsTime && diagram}
                <Flexbox flexDirection={'row'}>
                    <Flexbox flexDirection={'column'} className={columnStyles}>
                        <InfoBoxLabel
                            label={`Interval Size (${unitsLabel})`}
                            message={InfoMessages.dimensionInterval}
                        />
                        <RIENumber
                            value={dimension.data_transformation.interval}
                            change={this.onNumberUpdate}
                            fieldPath={[...baseDimensionPath, 'data_transformation', 'interval']}
                            processId={this.props.process.name}
                            processErrors={this.props.processErrors}
                            formDisplay
                            isDisabled={!this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    <Flexbox flexDirection="column" className={columnStyles}>
                        <InfoBoxLabel
                            label={`Start (${unitsLabel})`}
                            message={InfoMessages.dimension_start}
                        />
                        <RIENumber
                            value={dimension.data_transformation.start}
                            change={this.onNumberUpdate}
                            fieldPath={[...baseDimensionPath, 'data_transformation', 'start']}
                            processId={this.props.process.name}
                            processErrors={this.props.processErrors}
                            formDisplay
                            isDisabled={!this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    {!this.props.dimensionIsTime &&
                        <Flexbox flexDirection="column" className={columnStyles}>
                            <InfoBoxLabel
                                label={`End (${unitsLabel})`}
                                message={InfoMessages.dimension_end}
                            />
                            <RIENumber
                                value={dimension.data_transformation.end}
                                change={this.onNumberUpdate}
                                fieldPath={[...baseDimensionPath, 'data_transformation', 'end']}
                                processId={this.props.process.name}
                                processErrors={this.props.processErrors}
                                formDisplay
                                isDisabled={!this.props.hasWritePermissions}
                            />
                        </Flexbox>
                    }
                </Flexbox>
            </Flexbox>
        );
    };

    renderUnits = () => {
        let content = null;
        const dimension = this.getDimension();
        if (this.props.dimensionIsTime) {
            content = (
                <p>Per <a href="https://www.arm.gov/publications/programdocs/doe-sc-arm-15-004.pdf" target="_blank" rel="noopener noreferrer">
                ARM standards</a>,
                time is always measured in seconds.</p>
            );
        } else {
            content = (
                <Flexbox flexDirection={'row'}>
                    <p className={cx(this.boldFont, css`white-space: nowrap; margin-right: 5px;`)}>What units should be used for <span className={css`color:${colorLabelBlue}`}>{this.props.dimensionName}</span> values?</p>
                    <RIEUnits
                        value={Constants.convertValueToSelectValue(dimension.coordinate_variable_units)}
                        change={this.onFieldUpdated}
                        fieldPath={[...this.getBaseDimensionPath(), 'coordinate_variable_units']}
                        clearable={false}
                        options={this.getUnitOptions()}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        formDisplay
                        className={css`min-width: 250px !important;`}
                        isDisabled={!this.props.hasWritePermissions}
                    />
                </Flexbox>
            );
        }

        return <Flexbox flexDirection="row" className={css`margin-top: 15px;`}>{content}</Flexbox>;
    };

    render = () => {
        const radioLabelStyles = css`
            white-space: nowrap;
            margin-right: 5px;
            font-weight: normal;
            input {
                margin-right: 5px;
            }
        `;

        const isSameAsInput = this.isDimensionType(Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING);
        const isRegularGrid = this.isDimensionType(Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID);

        return (
            <Flexbox flexGrow={1} flexDirection={'column'}>
                <Flexbox className={this.boldFont}><p>How should <span className={css`color:${colorLabelBlue}`}>{this.props.dimensionName}</span> coordinate data be determined?</p></Flexbox>
                <Flexbox flexDirection={'column'} className={css`padding: 0 15px;`}>
                    <Flexbox flexDirection={'row'}>
                        {ComponentUtils.renderPermissionDependentComponent(
                            <label className={radioLabelStyles}>
                                <input
                                    type="radio"
                                    name="defineDimension"
                                    ref="input"
                                    value={'sameAsInput'}
                                    checked={isSameAsInput}
                                    tabIndex="1"
                                    onChange={this.onRadioClick}
                                    disabled={!this.props.hasWritePermissions}
                                />
                                Use the same {this.props.dimensionName} values as input datastream
                            </label>,
                            this.props.hasWritePermissions)
                        }
                        <DimensionInputDatasetField
                            coordinateSystemName={this.props.coordinateSystemName}
                            dimensionName={this.props.dimensionName}
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            displayPrimaryDatastreamOnly
                            forceFormDisplay
                            className={css`min-width: 200px !important;`}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    <Flexbox>
                        {ComponentUtils.renderPermissionDependentComponent(
                            <label className={radioLabelStyles}>
                                <input
                                    type="radio"
                                    name="defineDimension"
                                    value={Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID}
                                    checked={isRegularGrid}
                                    tabIndex="1"
                                    onChange={this.onRadioClick}
                                    disabled={!this.props.hasWritePermissions}
                                />
                                Specify regular interval
                            </label>,
                            this.props.hasWritePermissions)
                        }
                    </Flexbox>
                    {isRegularGrid && this.renderRegularInterval()}
                </Flexbox>
                {this.renderUnits()}
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/core/forms/DimensionsForm.js